import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import PropTypes from "prop-types";
import UserAvatar from "../../../../components/userAvatar/userAvatar";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { is12HourFormat } from "../../../../utils/formatters";
import { API_DATE_FORMAT } from "../../../../app_constants";
import AIAgent from "../../../ai";
import KBLearnMore from "../../../../components/learnMore/KBLearnMore";
import { ILEARN_URLS } from "../../../../constants/ilearnUrls";

dayjs.extend(relativeTime);

const ClientsInfo = () => {
  const { chaseIqEnabled, isAdmin, permissions, client_uid, userPageShortUrl } = useSelector(
    (state) => state.clientProfileModule.currentUser
  );
  const hasAIAccess = chaseIqEnabled && (isAdmin || permissions?.includes("dashboard_chase_iq"));

  const {
    clientInfo: {
      clientFirstName,
      clientLastName,
      clientPhone,
      clientEmail,
      clientId,
      clientRssid,
      clientCity,
      clientStatus,
      clientReferralSource,
      clientBirthDay,
      crossRegionalStoreName
    } = {},
    quickFacts: {
      clientProfileCreationDate,
      firstDataDate,
      firstVisitDate,
      lastVisitDate,
      lifetimeSpendPerVisit,
      lifetimeSpend,
      lifetimeSpendAvg,
      visitsCount,
      favoriteClass,
      favoriteStaffMember,
      prospect_type
    } = {},
    currentInformation: {
      membershipStatus,
      membershipStatusLabel,
      memberSince,
      membershipExpires,
      nextScheduledVisit,
      nextScheduledVisitClassName,
      nextScheduledAutopay,
      nextScheduledAutopayDate,
      lastActivePricingOption,
      lastActivePricingOptionExpires,
      lastActiveMembershipPricingOption,
      lastActiveMembershipPricingOptionExpires
    } = {}
  } = useSelector((state) => state.clientProfileModule.clientInfo);

  const { dateFormat, dateTimeFormat } = useSelector(
    (state) => state.clientProfileModule.currentUser
  );

  const [showCloseButton, setShowButton] = useState(false);
  useEffect(() => {
    if (window.name === "_client_profile") {
      setShowButton(true);
    }
  }, []);

  return (
    <>
      <div className={`client-info-upper${hasAIAccess ? " has-ai" : ""}`}>
        <div className="left">
          <div className="user-avatar">
            <UserAvatar firstName={clientFirstName} lastName={clientLastName} />
          </div>
          <div className="client-details">
            <div className="info-details">
              <div className="detail-item">
                <p className="name">Phone</p>
                <p className="value">
                  <a href={`tel:${clientPhone}`}>{clientPhone}</a>
                </p>
              </div>
              <div className="detail-item">
                <p className="name">Email</p>
                <p className="value">
                  <a href={`mailto:${clientEmail}`}>{clientEmail}</a>
                </p>
              </div>
              <div className="detail-item">
                <p className="name">Client ID</p>
                <p className="value">{clientId}</p>
              </div>
              {clientRssid ? (
                <div className="detail-item">
                  <p className="name">RSSID</p>
                  <p className="value">{clientRssid}</p>
                </div>
              ) : null}
              {clientCity ? (
                <div className="detail-item">
                  <p className="name">Home City</p>
                  <p className="value">{clientCity.toUpperCase()}</p>
                </div>
              ) : null}
              {crossRegionalStoreName ? (
                <div className="detail-item">
                  <p className="name">Home Site</p>
                  <p className="value">{crossRegionalStoreName}</p>
                </div>
              ) : null}
              {clientStatus ? (
                <div className="detail-item">
                  <p className="name">Status</p>
                  <p className="value">{clientStatus}</p>
                </div>
              ) : null}
              <div className="detail-item">
                {showCloseButton ? (
                  <a onClick={() => window.close()} className="btn-raw-data" href="#">
                    <FontAwesomeIcon
                      className="minus-icon"
                      icon={faTimesCircle}
                      style={{ color: "#8ac934" }}
                    />{" "}
                    Close View
                  </a>
                ) : null}

                <KBLearnMore
                  title="Client Profile"
                  description="This report shows a complete view of an individual client's activity, including visits, purchases, and account details. These insights help you better understand client behavior, support personalized service, and quickly access key information when assisting members."
                  kbUrl={ILEARN_URLS.clientProfile.clientProfiles}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="right">
          <p className="client-name">{`${clientFirstName} ${clientLastName}`} </p>
          <div className="quick-facts">
            <div className="facts-left">
              <p className="title">
                Quick Facts{" "}
                <span className="since">
                  &nbsp;&nbsp;since {dayjs(firstDataDate).format(dateFormat)}
                </span>
              </p>

              {clientProfileCreationDate ? (
                <p className="fact">
                  <span className="name">Client Profile Created: </span>
                  {dayjs(clientProfileCreationDate).format(
                    `${dateFormat} [at] ${is12HourFormat(dateTimeFormat)}`
                  )}{" "}
                  (
                  {dayjs().isAfter(dayjs(clientProfileCreationDate), "day")
                    ? dayjs(clientProfileCreationDate).endOf("day").fromNow()
                    : "Today"}
                  )
                </p>
              ) : null}

              {firstVisitDate ? (
                <p className="fact">
                  <span className="name">First Visit Date: </span>
                  {dayjs(firstVisitDate).format(dateFormat)} (
                  {dayjs().isAfter(dayjs(firstVisitDate), "day")
                    ? dayjs(firstVisitDate).endOf("day").fromNow()
                    : "Today"}
                  )
                </p>
              ) : null}

              {!hasAIAccess && lastVisitDate ? (
                <p className="fact">
                  <span className="name">Last Visit: </span>
                  {dayjs(lastVisitDate).format(dateFormat)} (
                  {dayjs().isAfter(dayjs(lastVisitDate), "day")
                    ? dayjs(lastVisitDate).endOf("day").add(1, "days").fromNow()
                    : "Today"}
                  )
                </p>
              ) : null}

              <p className="fact">
                <span className="name">Lifetime Spend Per Visit: </span>
                {lifetimeSpendPerVisit}
              </p>
              {!hasAIAccess ? (
                <p className="fact">
                  <span className="name">Lifetime Spend: </span>
                  {lifetimeSpend}
                </p>
              ) : null}

              {lifetimeSpendAvg ? (
                <p className="fact">
                  <span className="name">Lifetime Spend Avg. Per Month: </span>
                  {lifetimeSpendAvg}
                </p>
              ) : null}
            </div>

            <div className="facts-right">
              {!hasAIAccess && membershipStatus ? (
                <p className="fact">
                  <span className="name">Membership Status{membershipStatusLabel}: </span>
                  {membershipStatus}
                </p>
              ) : null}

              {!hasAIAccess && memberSince ? (
                <p className="fact">
                  <span className="name">Member Since: </span>
                  {memberSince}
                </p>
              ) : null}

              {!hasAIAccess && membershipExpires ? (
                <p className="fact">
                  <span className="name">Membership Expires: </span>
                  {membershipExpires}
                </p>
              ) : null}

              <p className="fact">
                <span className="name">Total Visits: </span>
                {visitsCount}
              </p>

              {clientReferralSource ? (
                <p className="fact">
                  <span className="name">Referral Source: </span>
                  {clientReferralSource}
                </p>
              ) : null}

              {!hasAIAccess ? (
                <p className="fact">
                  <span className="name">Favorite Class (last 3 months): </span>
                  {favoriteClass || "Not Available"}
                </p>
              ) : null}

              {!hasAIAccess ? (
                <p className="fact">
                  <span className="name">Favorite Instructor (last 3 months): </span>
                  {favoriteStaffMember || "Not Available"}
                </p>
              ) : null}

              <p className="fact">
                <span className="name">Prospect Type: </span>
                {prospect_type || "Not Available"}
              </p>

              {clientBirthDay &&
              dayjs(clientBirthDay) > new Date(1900, 1, 2, 0, 0) &&
              dayjs(clientBirthDay) < new Date() ? (
                <p className="fact">
                  <span className="name">Birthday: </span>
                  {dayjs(clientBirthDay).format(dateFormat)} (
                  {dayjs().diff(dayjs(clientBirthDay, API_DATE_FORMAT), "year")} years)
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      {hasAIAccess ? (
        <AIAgent
          pageName="client_profile"
          widget
          filter={{ client_uid, short_url: userPageShortUrl }}
        />
      ) : (
        <div className="client-info-mid-wrapper">
          <div className="client-info-mid">
            {nextScheduledVisit ? (
              <div className="cell">
                <p>Next Scheduled Visit</p>
                {nextScheduledVisit ? (
                  <p className="metric">
                    {nextScheduledVisitClassName},
                    <span className="highlight">
                      {" "}
                      {dayjs(nextScheduledVisit).endOf("day").fromNow()}.
                    </span>
                  </p>
                ) : (
                  <p className="metric">There are currently no visits scheduled for this client.</p>
                )}
              </div>
            ) : (
              ""
            )}
            {nextScheduledAutopay ? (
              <div className="cell">
                <p>Next Scheduled Autopay</p>
                {nextScheduledAutopay ? (
                  <p className="metric">
                    {nextScheduledAutopay},
                    <span className="highlight">
                      {" "}
                      {dayjs(nextScheduledAutopayDate).endOf("day").fromNow()}.
                    </span>
                  </p>
                ) : (
                  <p className="metric">
                    There are currently no autopays scheduled for this client.
                  </p>
                )}
              </div>
            ) : (
              ""
            )}
            {lastActivePricingOption ? (
              <div className="cell">
                <p>Last Active Sales Package</p>
                {lastActivePricingOption ? (
                  <div className="metric">
                    {lastActivePricingOption}
                    {lastActivePricingOptionExpires ? (
                      <div>
                        Expire
                        {dayjs(lastActivePricingOptionExpires) < dayjs() ? "d: " : "s: "}
                        <span className="highlight">
                          {dayjs(lastActivePricingOptionExpires).endOf("day").fromNow()}.
                        </span>
                      </div>
                    ) : (
                      ""
                    )}
                  </div>
                ) : (
                  <p className="metric">There are currently no sales for this client.</p>
                )}
              </div>
            ) : (
              ""
            )}
            {lastActiveMembershipPricingOption ? (
              <div className="cell">
                <p>Last Active Membership Sales Package</p>
                {lastActiveMembershipPricingOption ? (
                  <div className="metric">
                    {lastActiveMembershipPricingOption}
                    {lastActiveMembershipPricingOptionExpires ? (
                      <div>
                        Expire
                        {dayjs(lastActivePricingOptionExpires) < dayjs() ? "d: " : "s: "}
                        <span className="highlight">
                          {dayjs(lastActivePricingOptionExpires).endOf("day").fromNow()}.
                        </span>
                      </div>
                    ) : (
                      ""
                    )}
                  </div>
                ) : (
                  <p className="metric">There are currently no sales for this client.</p>
                )}
              </div>
            ) : (
              ""
            )}
          </div>
        </div>
      )}
    </>
  );
};

ClientsInfo.propTypes = {
  clientInfo: PropTypes.object,
  quickFacts: PropTypes.object,
  currentInfo: PropTypes.object
};

export default ClientsInfo;
